import { WebViewerInstance, Core } from '@pdftron/webviewer';

import {
  ANNOTATION_BORDER_WIDTH,
  ANNOTATION_COLOR_CODES,
  ANNOTATION_OPACITY,
  SUGGESTION_SUBJECT,
} from './consts';
import type {
  SuggestionAnnotationRaw,
  SuggestionAnnotationState,
} from './types';

/**
 * We cannot access/extend the CustomAnnotation class from the Core WebViewer object until it exists
 * at runtime. This is our second best option to get a strong typing for the SuggestionAnnotation class that we
 * define in buildSuggestionAnnotationClass.
 */
export interface ISuggestionAnnotation
  extends Core.Annotations.CustomAnnotation {
  show(): void;
  hide(): void;
  focus(): void;
  unfocus(): void;
  dim(): void;
  undim(): void;
  whiteout(): void;
  unwhiteout(): void;
  isDimmed(): boolean;
  isWhiteout(): boolean;
  isFocused(): boolean;
  getAppState(): SuggestionAnnotationState;
  setAppState(state: Partial<SuggestionAnnotationState>): void;
  rawAnnotation: SuggestionAnnotationRaw;
  partFileUuid: string;
}

export type SuggestionAnnotationClassType = new (
  rawAnnotation: SuggestionAnnotationRaw,
) => ISuggestionAnnotation;

// ============================================================================
// CORE DRAWING FUNCTIONS
// ============================================================================
// Low-level, reusable drawing primitives that handle basic canvas operations

/**
 * Converts a hex color to hex with alpha value
 * @param hexColor - Hex color string (e.g., "#8C41ED")
 * @param alpha - Alpha value between 0 and 1
 * @returns Hex color with alpha (e.g., "#8C41ED55")
 */
function hexWithAlpha(hexColor: string, alpha: number): string {
  // Convert alpha (0-1) to hex (00-FF)
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hexColor.slice(0, 7)}${alphaHex}`;
}

/**
 * Draws a fill with the specified color and opacity
 */
function drawFill(
  ctx: CanvasRenderingContext2D,
  color: string,
  opacity: number,
) {
  const fillColor = hexWithAlpha(color, opacity);
  ctx.fillStyle = fillColor;
  ctx.fill();
}

/**
 * Draws an outline with the specified color and opacity
 * Scales line width with zoom to maintain consistent appearance
 */
function drawOutline(
  ctx: CanvasRenderingContext2D,
  color: string,
  opacity: number,
  zoom: number,
  lineWidth = 1,
) {
  const strokeColor = hexWithAlpha(color, opacity);
  ctx.strokeStyle = strokeColor;
  // Scale the line width based on zoom level for consistent visual appearance
  ctx.lineWidth = lineWidth / zoom;
  ctx.stroke();
}

/**
 * Draws realistic blood dripping effect with more organic, viscous appearance
 */
function drawRealisticBloodDrips(
  ctx: CanvasRenderingContext2D,
  bounds: AnnotationBounds,
) {
  const bloodOrange = ANNOTATION_COLOR_CODES.BLEEDING_ORANGE; // Deep orange color
  const bloodOrangeLight = ANNOTATION_COLOR_CODES.BLEEDING_ORANGE_LIGHT; // Lighter orange for highlights
  const { x, y, width, height } = bounds;
  const bottomY = y + height;
  const borderRadius = 5;

  // Calculate valid range accounting for border radius
  const minX = x + borderRadius;
  const maxX = x + width - borderRadius;
  const validWidth = maxX - minX;

  // Create fewer but more prominent drips
  const dripCount = Math.max(2, Math.floor(width / 30));
  const dripData: Array<{
    dripX: number;
    dripLength: number;
    baseWidth: number;
  }> = [];

  for (let i = 0; i < dripCount; i++) {
    const seed = (x + i * 137) % 100;
    const dripX = minX + (seed / 100) * validWidth;

    // Vary drip characteristics more dramatically
    const lengthSeed = (x + i * 523) % 100;
    const dripLength = 12 + (lengthSeed / 100) * 20;

    const widthSeed = (x + i * 847) % 100;
    const baseWidth = 3 + (widthSeed / 100) * 5;

    // Store drip data for later droplet positioning
    dripData.push({ dripX, dripLength, baseWidth });

    // Draw main drip body with irregular edges
    ctx.beginPath();
    ctx.moveTo(dripX - baseWidth / 2, bottomY);

    // Create organic curves with multiple control points for irregular edges
    const segments = 5;
    let currentX = dripX - baseWidth / 2;
    let currentY = bottomY;

    for (let seg = 0; seg <= segments; seg++) {
      const t = seg / segments;
      const segmentY = bottomY + dripLength * t;

      // Taper width as we go down, with some irregularity
      const irregularitySeed = (x + i * 100 + seg * 50) % 100;
      const irregularity = 0.8 + (irregularitySeed / 100) * 0.4;
      const segmentWidth = baseWidth * (1 - t * 0.7) * irregularity;

      // Add slight wobble to left edge
      const wobbleSeed = (x + i * 200 + seg * 30) % 100;
      const wobble = (wobbleSeed / 100 - 0.5) * baseWidth * 0.3;

      if (seg === 0) {
        currentX = dripX - segmentWidth / 2 + wobble;
        currentY = segmentY;
      } else if (seg === segments) {
        // End in a point
        ctx.quadraticCurveTo(currentX, currentY, dripX, segmentY);
      } else {
        const nextX = dripX - segmentWidth / 2 + wobble;
        ctx.quadraticCurveTo(currentX, currentY, nextX, segmentY);
        currentX = nextX;
        currentY = segmentY;
      }
    }

    // Draw right side back up
    currentX = dripX;
    currentY = bottomY + dripLength;

    for (let seg = segments; seg >= 0; seg--) {
      const t = seg / segments;
      const segmentY = bottomY + dripLength * t;

      const irregularitySeed = (x + i * 100 + seg * 50) % 100;
      const irregularity = 0.8 + (irregularitySeed / 100) * 0.4;
      const segmentWidth = baseWidth * (1 - t * 0.7) * irregularity;

      const wobbleSeed = (x + i * 200 + seg * 30 + 1000) % 100;
      const wobble = (wobbleSeed / 100 - 0.5) * baseWidth * 0.3;

      if (seg === 0) {
        ctx.quadraticCurveTo(
          currentX,
          currentY,
          dripX + baseWidth / 2,
          bottomY,
        );
      } else {
        const nextX = dripX + segmentWidth / 2 + wobble;
        ctx.quadraticCurveTo(currentX, currentY, nextX, segmentY);
        currentX = nextX;
        currentY = segmentY;
      }
    }

    ctx.closePath();

    // Fill with gradient effect using multiple layers
    drawFill(ctx, bloodOrange, 0.85);

    // Add highlights/texture with lighter color in center
    ctx.beginPath();
    ctx.moveTo(dripX - baseWidth / 4, bottomY);
    ctx.lineTo(dripX - baseWidth / 8, bottomY + dripLength * 0.7);
    ctx.lineTo(dripX, bottomY + dripLength);
    ctx.lineTo(dripX + baseWidth / 8, bottomY + dripLength * 0.7);
    ctx.lineTo(dripX + baseWidth / 4, bottomY);
    ctx.closePath();
    drawFill(ctx, bloodOrangeLight, 0.4);
  }

  // Add continuous thin film of blood along the bottom edge
  // This mimics the accumulation that forms before drips fall
  ctx.beginPath();
  ctx.moveTo(minX, bottomY);

  // Use smooth sine wave with subtle fluctuations
  const filmSegments = Math.max(20, Math.floor(validWidth / 4));
  const maxThickness = 3.5;

  for (let i = 0; i <= filmSegments; i++) {
    const t = i / filmSegments;
    const segmentX = minX + t * validWidth;

    // Main sine wave envelope - starts at 0, peaks in middle, returns to 0
    const sineEnvelope = Math.sin(t * Math.PI);
    const baseThickness = sineEnvelope * maxThickness;

    // Add subtle high-frequency fluctuations (like a ripple filter)
    const fluctuationFrequency = 8;
    const fluctuationSeed = (x + i * 127) % 100;
    const fluctuationPhase = (fluctuationSeed / 100) * Math.PI * 0.5;
    const fluctuation =
      Math.sin(t * Math.PI * fluctuationFrequency + fluctuationPhase) * 0.8;

    // Combine envelope with fluctuations, ensuring smooth taper at edges
    const edgeY = bottomY + baseThickness + fluctuation * sineEnvelope;

    if (i === 0) {
      ctx.lineTo(segmentX, edgeY);
    } else {
      ctx.lineTo(segmentX, edgeY);
    }
  }

  // Close the path back along the bottom edge
  ctx.lineTo(maxX, bottomY);
  ctx.lineTo(minX, bottomY);
  ctx.closePath();

  // Draw with high opacity
  drawFill(ctx, bloodOrange, 0.88);

  // Add a second layer with lighter color for depth/shininess
  ctx.beginPath();
  ctx.moveTo(minX, bottomY);

  for (let i = 0; i <= filmSegments; i++) {
    const t = i / filmSegments;
    const segmentX = minX + t * validWidth;

    const sineEnvelope = Math.sin(t * Math.PI);
    const baseThickness = sineEnvelope * maxThickness;

    const fluctuationFrequency = 8;
    const fluctuationSeed = (x + i * 127) % 100;
    const fluctuationPhase = (fluctuationSeed / 100) * Math.PI * 0.5;
    const fluctuation =
      Math.sin(t * Math.PI * fluctuationFrequency + fluctuationPhase) * 0.8;

    // Draw highlight at 50% of thickness
    const highlightY =
      bottomY + (baseThickness + fluctuation * sineEnvelope) * 0.5;

    if (i === 0) {
      ctx.lineTo(segmentX, highlightY);
    } else {
      ctx.lineTo(segmentX, highlightY);
    }
  }

  ctx.lineTo(maxX, bottomY);
  ctx.lineTo(minX, bottomY);
  ctx.closePath();
  drawFill(ctx, bloodOrangeLight, 0.35);

  // Add realistic teardrop droplets at the end of each drip
  for (let i = 0; i < dripData.length; i++) {
    const { dripX, dripLength, baseWidth } = dripData[i];
    const seed = (x + i * 419) % 100;

    // Position droplet at the end of the drip with slight offset
    const dropletX = dripX + (seed / 100 - 0.5) * baseWidth * 0.3;
    const dropletY = bottomY + dripLength + 2 + (seed / 100) * 3;
    const dropletRadius = 1.5 + (seed / 100) * 2.5;

    // Draw teardrop shape with organic curves
    ctx.beginPath();
    // Start at the point (top of teardrop)
    const pointY = dropletY - dropletRadius * 1.4;
    ctx.moveTo(dropletX, pointY);

    // Right side curve with slight irregularity
    const irregularitySeed = (seed + i * 50) % 100;
    const rightBulge = 0.65 + (irregularitySeed / 100) * 0.15;
    ctx.bezierCurveTo(
      dropletX + dropletRadius * 0.6,
      dropletY - dropletRadius * 0.9,
      dropletX + dropletRadius * rightBulge,
      dropletY - dropletRadius * 0.3,
      dropletX + dropletRadius * rightBulge,
      dropletY,
    );

    // Bottom arc (right to left)
    ctx.arc(dropletX, dropletY, dropletRadius * rightBulge, 0, Math.PI, false);

    // Left side curve
    const leftBulge = 0.65 + (((irregularitySeed + 50) % 100) / 100) * 0.15;
    ctx.bezierCurveTo(
      dropletX - dropletRadius * leftBulge,
      dropletY - dropletRadius * 0.3,
      dropletX - dropletRadius * 0.6,
      dropletY - dropletRadius * 0.9,
      dropletX,
      pointY,
    );

    ctx.closePath();
    drawFill(ctx, bloodOrange, 0.8);

    // Add subtle highlight on larger drops
    if (dropletRadius > 2.5) {
      ctx.beginPath();
      ctx.ellipse(
        dropletX + dropletRadius * 0.15,
        dropletY - dropletRadius * 0.2,
        dropletRadius * 0.2,
        dropletRadius * 0.3,
        0,
        0,
        Math.PI * 2,
      );
      ctx.closePath();
      drawFill(ctx, bloodOrangeLight, 0.3);
    }
  }
}

// ============================================================================
// UNIFIED ANNOTATION DRAWING FUNCTION
// ============================================================================

type AnnotationState =
  | 'whiteout'
  | 'whiteout-hover'
  | 'dimmed'
  | 'default'
  | 'hover'
  | 'focus';

interface AnnotationBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

type DrawAnnotationParams = {
  ctx: CanvasRenderingContext2D;
  bounds: AnnotationBounds;
  mainColor: string;
  mainOpacity: number;
  mainBorderWidth: number;
  state: AnnotationState;
  zoom: number;
  bleedOrange: boolean;
};

/**
 * Annotation drawing function that handles all states
 */
function drawAnnotation({
  ctx,
  bounds,
  mainColor,
  mainOpacity,
  mainBorderWidth,
  state,
  zoom,
  bleedOrange,
}: DrawAnnotationParams) {
  ctx.save();

  // Set blend mode based on state
  if (state === 'whiteout' || state === 'whiteout-hover') {
    ctx.globalCompositeOperation = 'source-over';
  } else {
    ctx.globalCompositeOperation = 'darken';
  }

  const color = bleedOrange
    ? ANNOTATION_COLOR_CODES.BLEEDING_ORANGE
    : mainColor;

  // Create rounded rectangle path
  const { x, y, width, height } = bounds;
  const radius = 3; // Standard corner radius
  const adjustedRadius = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + adjustedRadius, y);
  ctx.lineTo(x + width - adjustedRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + adjustedRadius);
  ctx.lineTo(x + width, y + height - adjustedRadius);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - adjustedRadius,
    y + height,
  );
  ctx.lineTo(x + adjustedRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - adjustedRadius);
  ctx.lineTo(x, y + adjustedRadius);
  ctx.quadraticCurveTo(x, y, x + adjustedRadius, y);
  ctx.closePath();

  // Apply state-specific drawing logic
  // some states override opacity and border width - whiteout, focus, dim, hover, etc.
  switch (state) {
    case 'whiteout': {
      // Draw 95% fill to cover the content
      const whiteoutColor = '#FFFFFF';
      drawFill(ctx, whiteoutColor, 0.95);
      break;
    }

    case 'whiteout-hover': {
      // Then draw 15% fill so people can still know the category
      drawFill(ctx, color, mainOpacity);
      // Add hover outline
      drawOutline(
        ctx,
        color,
        ANNOTATION_OPACITY.DEFAULT_OUTLINE,
        zoom,
        ANNOTATION_BORDER_WIDTH.HOVER,
      );
      break;
    }

    case 'dimmed':
      // Draw fill with dimmed opacity, no outline
      drawFill(ctx, color, mainOpacity);
      break;

    case 'default':
      // Draw fill with default opacity and 1px outline
      drawFill(ctx, color, mainOpacity);
      if (mainBorderWidth > 0) {
        drawOutline(
          ctx,
          color,
          ANNOTATION_OPACITY.DEFAULT_OUTLINE,
          zoom,
          bleedOrange
            ? ANNOTATION_BORDER_WIDTH.BLEEDING_ORANGE
            : mainBorderWidth,
        );
      }

      break;

    case 'hover':
      // Draw fill with hover opacity (15%) and 3px outline
      drawFill(ctx, color, mainOpacity);
      drawOutline(
        ctx,
        color,
        ANNOTATION_OPACITY.DEFAULT_OUTLINE,
        zoom,
        ANNOTATION_BORDER_WIDTH.HOVER,
      );
      break;

    case 'focus':
      // Draw fill with focused opacity (30%) and 3px outline
      drawFill(ctx, color, ANNOTATION_OPACITY.FOCUS);
      drawOutline(
        ctx,
        color,
        ANNOTATION_OPACITY.DEFAULT_OUTLINE,
        zoom,
        ANNOTATION_BORDER_WIDTH.FOCUS,
      );
      break;
  }

  // Apply blood drip effect if specified
  if (bleedOrange) {
    drawRealisticBloodDrips(ctx, bounds);
  }

  ctx.restore();
}

export const buildSuggestionAnnotationClass = ({
  webViewerInstance,
  annotationManager,
}: {
  webViewerInstance: WebViewerInstance;
  annotationManager: Core.AnnotationManager;
}): SuggestionAnnotationClassType => {
  const { Annotations } = webViewerInstance.Core;

  class CustomSelectionModel extends Annotations.SelectionModel {
    constructor(
      annotation: Core.Annotations.Annotation,
      canModify: boolean,
      isSelected: boolean,
      docViewer: Core.DocumentViewer,
    ) {
      super(annotation, canModify, isSelected, docViewer);
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */
    drawSelectionOutline(
      ctx: CanvasRenderingContext2D,
      annotation: Core.Annotations.Annotation,
      zoom?: number,
    ) {
      // we intentionally do not want to draw anything here
    }
  }

  class SuggestionAnnotation
    extends Annotations.CustomAnnotation
    implements ISuggestionAnnotation
  {
    documentViewer: Core.DocumentViewer;
    annotationManager: Core.AnnotationManager;
    rawAnnotation: SuggestionAnnotationRaw;
    partFileUuid: string;
    colorCode: string;
    opacity: number;
    borderWidth: number;
    private _isBleedingOrange: boolean;
    private _isFocused: boolean;
    private _isWhiteout: boolean;
    private _isDimmed: boolean;

    constructor(rawAnnotation: SuggestionAnnotationRaw) {
      super('suggestion'); // provide the custom XFDF element name
      this.Subject = SUGGESTION_SUBJECT;
      // NB: No matter what, TypeScript does not play nice with CustomSelectionModel and the typedef in Core.Annotations.SelectionModel
      // FIXME: Check on this after the Apryse/PDFTron v11 update and see if they're more in sync.
      this.selectionModel =
        CustomSelectionModel as unknown as Core.Annotations.SelectionModel;
      this.documentViewer = webViewerInstance.Core.documentViewer;
      this.annotationManager = this.documentViewer.getAnnotationManager();
      this.rawAnnotation = rawAnnotation;
      this.colorCode = rawAnnotation.colorCode;
      this.opacity = rawAnnotation.opacity;
      this.borderWidth = rawAnnotation.borderWidth;
      this.partFileUuid = rawAnnotation.partFileId;
      // Limit interaction with the annotations
      this.NoMove = true;
      this.NoResize = true;
      this.NoRotate = true;
      this.NoDelete = true;
      this.Printable = false; // Exclude from prints
      this.BlendMode = 'darken'; // to avoid hiding content or cursing the color
      this.IsHoverable = true; // Enable hover detection
      this._isFocused = false;
      this._isWhiteout = false;
      this._isDimmed = false;
      this._isBleedingOrange = false;
    }

    show() {
      this.Hidden = false;
    }

    hide() {
      this.Hidden = true;
    }

    focus() {
      this._isFocused = true;
    }

    unfocus() {
      this._isFocused = false;
    }

    whiteout() {
      this._isWhiteout = true;
    }

    unwhiteout() {
      this._isWhiteout = false;
    }

    dim() {
      this._isDimmed = true;
    }

    undim() {
      this._isDimmed = false;
    }

    isDimmed() {
      return this._isDimmed;
    }

    isWhiteout() {
      return this._isWhiteout;
    }

    isFocused() {
      return this._isFocused;
    }

    getAppState() {
      return {
        isHidden: this.Hidden,
        isFocused: this._isFocused,
        isWhiteout: this._isWhiteout,
        isDimmed: this._isDimmed,
        colorCode: this.colorCode,
        opacity: this.opacity,
        borderWidth: this.borderWidth,
        isBleedingOrange: this._isBleedingOrange,
      };
    }

    setAppState(state: Partial<SuggestionAnnotationState>) {
      if (state.isHidden !== undefined) {
        this.Hidden = state.isHidden;
      }
      if (state.isFocused !== undefined) {
        this._isFocused = state.isFocused;
      }
      if (state.isWhiteout !== undefined) {
        this._isWhiteout = state.isWhiteout;
      }
      if (state.isDimmed !== undefined) {
        this._isDimmed = state.isDimmed;
      }
      if (state.colorCode !== undefined) {
        this.colorCode = state.colorCode;
      }
      if (state.opacity !== undefined) {
        this.opacity = state.opacity;
      }
      if (state.borderWidth !== undefined) {
        this.borderWidth = state.borderWidth;
      }
      if (state.isBleedingOrange !== undefined) {
        this._isBleedingOrange = state.isBleedingOrange;
      }
    }

    draw(ctx: CanvasRenderingContext2D, pageMatrix: number[]) {
      const zoom = this.documentViewer.getZoomLevel() || 1;

      ctx.save();
      // Apply the pageMatrix transformation
      ctx.setTransform(
        pageMatrix[0],
        pageMatrix[1],
        pageMatrix[2],
        pageMatrix[3],
        pageMatrix[4],
        pageMatrix[5],
      );

      // if focused bring to front
      if (this._isFocused) {
        this.annotationManager.bringToFront(this);
      }
      // if whiteout make unlistable using built in PDFTron property
      if (this._isWhiteout) {
        this.Listable = false;
      } else {
        this.Listable = true;
      }

      // Check if annotation is being hovered using PDFTron's IsHovering property
      const isHovered = this.IsHovering;

      // Determine annotation state and use unified drawing function
      let annotationState: AnnotationState;
      if (this._isFocused && isHovered) {
        annotationState = 'focus';
      } else if (this._isFocused) {
        annotationState = 'focus';
      } else if (this._isWhiteout && isHovered) {
        annotationState = 'whiteout-hover';
      } else if (this._isWhiteout) {
        annotationState = 'whiteout';
      } else if (isHovered) {
        annotationState = 'hover';
      } else if (this._isDimmed) {
        annotationState = 'dimmed';
      } else {
        annotationState = 'default';
      }

      const bounds: AnnotationBounds = {
        x: this.X,
        y: this.Y,
        width: this.Width,
        height: this.Height,
      };

      drawAnnotation({
        ctx,
        bounds,
        mainColor: this.colorCode,
        mainOpacity: this.opacity,
        mainBorderWidth: this.borderWidth,
        state: annotationState,
        zoom,
        bleedOrange: this._isBleedingOrange,
      });

      ctx.restore();
    }
  }
  SuggestionAnnotation.prototype.elementName = 'Suggestion';

  // Register the annotation type with the annotation manager
  annotationManager.registerAnnotationType(
    SuggestionAnnotation.prototype.elementName,
    SuggestionAnnotation,
  );

  return SuggestionAnnotation;
};
